import boxConfigurations from '@data/boxConfigurations.json'

import type { ShopifyBasicOrder } from '@lib/queries/orders/get-basic-orders-query'
import type { BoxConfigurationRMC } from '@lib/types/box-configuration'
import { calculateHoursDifference, calculateHoursSince } from '@lib/utils/dates'

/**
 * Finds the box configuration for a given SKU
 *
 * @param sku - The product SKU to find configuration for
 * @returns The box configuration object or null if not found
 */
function findBoxConfiguration(sku: string) {
	return boxConfigurations.find((config) =>
		config.product_skus.some((productSku) => productSku.sku === sku)
	)
}

/**
 * Formats the remaining time into a human-readable message
 *
 * @param hoursLeft - Number of hours remaining
 * @returns Formatted message (e.g. "2 days left" or "5 hours left")
 */
function formatTimeLeftMessage(hoursLeft: number): string {
	if (hoursLeft > 48) {
		return `${Math.floor(hoursLeft / 24)} days left`
	}
	return `${Math.floor(hoursLeft)} hours left`
}

const DUNNING_CUSTOMIZATION_HOURS = 72

function parseUTCTime(timeStr: string): { hours: number; minutes: number } {
	const [h, m] = timeStr.split(':').map(Number)
	return { hours: h, minutes: m }
}

function getRMCDates(rmc: BoxConfigurationRMC): { start: Date; close: Date } {
	if (rmc.use_custom_dates) {
		return {
			start: new Date(rmc.customization_start_date),
			close: new Date(rmc.customization_close_date)
		}
	}
	const now = new Date()
	const y = now.getUTCFullYear()
	const m = now.getUTCMonth()
	const startDay = rmc.default_start_day ?? 1
	const endDay = rmc.default_end_day ?? 5
	const { hours: sh, minutes: sm } = parseUTCTime(rmc.default_start_time ?? '07:00')
	const { hours: eh, minutes: em } = parseUTCTime(rmc.default_end_time ?? '06:59')
	return {
		start: new Date(Date.UTC(y, m, startDay, sh, sm, 0)),
		close: new Date(Date.UTC(y, m, endDay, eh, em, 59))
	}
}

/**
 * Customization logic hook for subscription boxes
 *
 * This hook determines the customization status and timing for both New Member Customization (NMC)
 * and Recurring Member Customization (RMC) boxes. It calculates remaining customization time
 * and determines if a box can be customized based on various conditions.
 *
 * For RMC (Recurring Member Customization):
 * - Customization is available between customization_start_date and customization_close_date (Prismic)
 *
 * For dunning orders (isDunning = true):
 * - Bypasses the Prismic window entirely
 * - Customization is allowed for DUNNING_CUSTOMIZATION_HOURS (72h) after processedAt
 *
 * For NMC (New Member Customization):
 * - Customization is available for a specified number of hours after order processing
 *
 * @param chargeShopifyOrder - The Shopify order object containing order details and tags
 * @param numberOrders - The number of orders the customer has placed (1 for NMC, >1 for RMC)
 * @param subscriptionProductSKU - The SKU of the subscription product
 * @param isDunning - Whether this order is a dunning recovery (bypasses Prismic window)
 * @returns An object containing:
 *   - customizationType: 'NMC' | 'RMC' | null - The type of customization available
 *   - customizationHoursLeft: number - Hours remaining to customize the box
 *   - timeLeftMessage: string - Formatted message showing time remaining
 *   - boxCustomized: boolean - Whether the box has been fully customized
 *   - boxPartiallyCustomized: boolean - Whether the box has been partially customized
 *   - customizableBox: boolean - Whether the box can be customized at this time
 */
export default function useCustomizationLogic(
	chargeShopifyOrder: ShopifyBasicOrder | null | undefined,
	numberOrders: number,
	subscriptionProductSKU: string,
	isDunning: boolean = false
) {
	// Early return if no order is provided
	if (!chargeShopifyOrder) {
		return {
			customizationType: null,
			customizationHoursLeft: null,
			timeLeftMessage: null,
			boxPartiallyCustomized: false,
			customizableBox: null
		}
	}

	// Find the box configuration for this SKU
	const boxConfig = findBoxConfiguration(subscriptionProductSKU)
	if (!boxConfig) {
		console.error(`No box configuration found for SKU: ${subscriptionProductSKU}`)
		return {
			customizationType: null,
			customizationHoursLeft: null,
			timeLeftMessage: null,
			boxPartiallyCustomized: false,
			customizableBox: null
		}
	}

	// Determine customization type based on number of orders
	let customizationType: 'NMC' | 'RMC' | null = null
	if (numberOrders === 1) {
		customizationType = 'NMC'
	} else if (numberOrders > 1) {
		customizationType = 'RMC'
	}

	// Initialize default values
	let customizationHoursLeft = 0
	let timeLeftMessage = ''
	let boxCustomized = true
	let boxPartiallyCustomized = false

	if (customizationType === 'RMC') {
		// Check if box has been customized or partially customized
		boxCustomized = chargeShopifyOrder.tags.includes('rmc_box_customized')
		boxPartiallyCustomized = chargeShopifyOrder.tags.includes('rmc_box_partial_customized')

		if (isDunning) {
			// Dunning orders bypass the Prismic window entirely.
			// Allow customization for DUNNING_CUSTOMIZATION_HOURS after the order was processed.
			const hoursSinceProcessed = calculateHoursSince(chargeShopifyOrder.processedAt)
			customizationHoursLeft = DUNNING_CUSTOMIZATION_HOURS - hoursSinceProcessed
		} else {
			// RMC customization window: computed from defaults or Prismic override dates
			const { start: RMCStartDate, close: RMCCloseDate } = getRMCDates(boxConfig.rmc as unknown as BoxConfigurationRMC)
			const isBeforeStartDate = new Date() < RMCStartDate
			const hoursToCloseDate = calculateHoursDifference(
				new Date().toISOString(),
				RMCCloseDate.toISOString()
			)

			const isCustomizationAvailable = !isBeforeStartDate && hoursToCloseDate > 0

			if (isCustomizationAvailable) {
				customizationHoursLeft = hoursToCloseDate
			}
		}

		timeLeftMessage = formatTimeLeftMessage(customizationHoursLeft)
	} else if (customizationType === 'NMC') {
		// NMC (New Member Customization) logic
		boxCustomized = chargeShopifyOrder.tags.includes('box_customized')
		boxPartiallyCustomized = false

		// NMC has a fixed window for customization based on configuration
		customizationHoursLeft =
			boxConfig.nmc.customization_hours - calculateHoursSince(chargeShopifyOrder.processedAt)
		timeLeftMessage = formatTimeLeftMessage(customizationHoursLeft)
	}

	// Determine if box can be customized based on current state
	const customizableBox = !boxCustomized && customizationHoursLeft > 0

	return {
		customizationType,
		customizationHoursLeft,
		timeLeftMessage,
		boxCustomized,
		boxPartiallyCustomized,
		customizableBox
	}
}
